import { z } from 'zod';

// Security validation schemas
// Lenient schema for current PIN verification (allows weak PINs)
export const lenientPinSchema = z.string()
  .regex(/^\d{6}$/, "PIN must be exactly 6 digits");

// Strict schema for new PINs (enforces security requirements)
export const strictPinSchema = z.string()
  .regex(/^\d{6}$/, "PIN must be exactly 6 digits")
  .refine((pin) => {
    // Additional security checks
    const hasRepeatingDigits = /(\d)\1{5}/.test(pin); // All same digits
    const isSequential = /012345|123456|234567|345678|456789|987654|876543|765432|654321|543210/.test(pin);
    const isCommonPin = ['123456', '000000', '111111', '222222', '333333', '444444', '555555', '666666', '777777', '888888', '999999'].includes(pin);
    
    return !hasRepeatingDigits && !isSequential && !isCommonPin;
  }, "PIN cannot be sequential digits, all same digits, or common patterns");

// Default PIN schema (backwards compatibility)
export const pinSchema = strictPinSchema;

export const emailSchema = z.string()
  .email("Invalid email format")
  .min(5, "Email must be at least 5 characters")
  .max(255, "Email must be less than 255 characters")
  .refine((email) => {
    // Additional email security checks
    const hasValidDomain = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const noScriptTags = !/<script|javascript:|data:/i.test(email);
    return hasValidDomain && noScriptTags;
  }, "Email format is invalid or contains unsafe content");

export const verificationCodeSchema = z.string()
  .regex(/^\d{6}$/, "Verification code must be exactly 6 digits")
  .transform((code) => code.trim()); // Sanitize whitespace

// Input sanitization utilities
export const sanitizeInput = (input: string): string => {
  if (typeof input !== 'string') return '';
  
  return input
    .trim()
    .replace(/[<>'"]/g, '') // Remove potential XSS characters
    .substring(0, 1000); // Limit length
};

export const sanitizePinInput = (input: string): string => {
  if (typeof input !== 'string') return '';
  
  // Only allow digits, remove everything else
  return input.replace(/[^\d]/g, '').substring(0, 6);
};

export const sanitizeEmail = (input: string): string => {
  if (typeof input !== 'string') return '';
  
  return input
    .trim()
    .toLowerCase()
    .replace(/[<>'"]/g, '') // Remove XSS characters
    .substring(0, 255); // Limit email length
};

// Rate limiting helpers
export const isRateLimited = (key: string, limit: number = 5, windowMs: number = 60000): boolean => {
  const now = Date.now();
  const windowKey = `${key}_${Math.floor(now / windowMs)}`;
  
  const attempts = JSON.parse(localStorage.getItem(windowKey) || '{"count": 0, "firstAttempt": 0}');
  
  if (attempts.count >= limit) {
    return true;
  }
  
  attempts.count++;
  if (attempts.firstAttempt === 0) {
    attempts.firstAttempt = now;
  }
  
  localStorage.setItem(windowKey, JSON.stringify(attempts));
  
  // Cleanup old entries
  for (let i = 0; i < localStorage.length; i++) {
    const storageKey = localStorage.key(i);
    if (storageKey && storageKey.startsWith(key) && storageKey !== windowKey) {
      const oldData = JSON.parse(localStorage.getItem(storageKey) || '{}');
      if (now - oldData.firstAttempt > windowMs) {
        localStorage.removeItem(storageKey);
      }
    }
  }
  
  return false;
};

// Security event logging (client-side)
export const logSecurityEvent = (eventType: string, details?: Record<string, any>) => {
  // Only log in development or for serious security events
  if (process.env.NODE_ENV === 'development') {
    console.warn(`Security Event: ${eventType}`, details);
  }
  
  // In production, you might want to send this to your analytics service
  // but be careful not to log sensitive information
  if (eventType === 'suspicious_activity' || eventType === 'rate_limit_exceeded') {
    // Log only non-sensitive security events in production
    console.warn(`Security Event: ${eventType}`);
  }
};

// Validation result type
export interface ValidationResult {
  isValid: boolean;
  error?: string;
  sanitizedValue?: string;
}

// Comprehensive validation function
export const validateAndSanitize = (
  input: string, 
  schema: z.ZodSchema, 
  sanitizer?: (input: string) => string
): ValidationResult => {
  try {
    // First sanitize if sanitizer provided
    const sanitizedInput = sanitizer ? sanitizer(input) : input;
    
    // Then validate
    const result = schema.parse(sanitizedInput);
    
    return {
      isValid: true,
      sanitizedValue: result
    };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return {
        isValid: false,
        error: error.errors[0]?.message || 'Validation failed'
      };
    }
    
    return {
      isValid: false,
      error: 'Validation failed'
    };
  }
};